import { useNavigate } from "react-router-dom";
import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { ALL_SERVICES } from "../../constant/HeaderData";
import Badge from "../common/Badge";
import MarqueeColumn from "./MarqueeColumn";
import NetworkOverlay from "./NetworkOverlay";
import RotatingWord from "./RotatingWord";
import colocationImg from "../../assets/HeroSectionImages/colocation_services.webp";
import dedicatedServersImg from "../../assets/HeroSectionImages/dedicated_servers.webp";
import cloudServicesImg from "../../assets/HeroSectionImages/cloud_services.webp";
import itConsultationImg from "../../assets/HeroSectionImages/it_consultation.webp";
import securityImg from "../../assets/HeroSectionImages/security.webp";
import engineersServiceImg from "../../assets/HeroSectionImages/engineers_service.webp";
import vpsServicesImg from "../../assets/HeroSectionImages/vps_services.webp";
import datacenterImg from "../../assets/HomeImages/datacenter.webp";
import teamOfficeImg from "../../assets/HomeImages/team-office.webp";
import dataCenterImg from "../../assets/Services/dataCenter.webp";
import networkConnectivityImg from "../../assets/Services/networkConnectivity.webp";
import serverSecurityImg from "../../assets/Services/serverSecurity.webp";

const MARQUEE_COLUMNS = [
  {
    images: [colocationImg, dedicatedServersImg, cloudServicesImg],
    duration: 28,
    reverse: false,
  },
  {
    images: [itConsultationImg, securityImg, engineersServiceImg],
    duration: 34,
    reverse: true,
  },
  {
    images: [vpsServicesImg, datacenterImg, teamOfficeImg],
    duration: 24,
    reverse: false,
  },
  {
    images: [dataCenterImg, networkConnectivityImg, serverSecurityImg],
    duration: 30,
    reverse: true,
  },
  {
    images: [colocationImg, securityImg, datacenterImg],
    duration: 26,
    reverse: false,
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const cardsContainerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const TiltCard = ({ service }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [8, -8]), {
    stiffness: 250,
    damping: 20,
  });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-8, 8]), {
    stiffness: 250,
    damping: 20,
  });

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      variants={cardVariants}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      whileHover={{ y: -6 }}
      style={{ rotateX, rotateY, transformPerspective: 800 }}
      className="rounded-2xl border border-white/15 bg-slate-950/60 p-5 backdrop-blur-md transition-colors hover:border-[rgba(var(--website-secondary-rgb),0.45)] hover:bg-slate-950/75"
    >
      <h2 className="text-base font-semibold text-white">{service.title}</h2>
      <p className="mt-2 text-sm leading-relaxed text-slate-300">
        {service.description}
      </p>
    </motion.div>
  );
};

const HeroSection = () => {
  const navigate = useNavigate();

  const handleContactClick = () => {
    navigate("/contact");
  };

  const parallaxX = useMotionValue(0);
  const parallaxY = useMotionValue(0);
  const springX = useSpring(parallaxX, { stiffness: 60, damping: 20 });
  const springY = useSpring(parallaxY, { stiffness: 60, damping: 20 });
  const imageX = useTransform(springX, [-0.5, 0.5], [-16, 16]);
  const imageY = useTransform(springY, [-0.5, 0.5], [-16, 16]);

  const handleSectionMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    parallaxX.set((e.clientX - rect.left) / rect.width - 0.5);
    parallaxY.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleSectionMouseLeave = () => {
    parallaxX.set(0);
    parallaxY.set(0);
  };

  return (
    <section
      className="relative w-full overflow-hidden bg-[var(--surface-dark)] py-8 md:min-h-[68vh] md:py-16"
      onMouseMove={handleSectionMouseMove}
      onMouseLeave={handleSectionMouseLeave}
    >
      <div className="absolute inset-0" aria-hidden="true">
        <motion.div
          style={{ x: imageX, y: imageY, scale: 1.08 }}
          className="grid h-full grid-cols-3 gap-4 opacity-95 sm:grid-cols-4 md:opacity-98 lg:grid-cols-5"
        >
          {MARQUEE_COLUMNS.map((column, index) => (
            <div
              key={index}
              className={index >= 4 ? "hidden lg:block" : index === 3 ? "hidden sm:block" : ""}
            >
              <MarqueeColumn
                images={column.images}
                duration={column.duration}
                reverse={column.reverse}
              />
            </div>
          ))}
        </motion.div>

        <div className="absolute inset-0 bg-gradient-to-r from-[rgba(var(--surface-dark-rgb),0.55)] via-[rgba(var(--surface-dark-rgb),0.4)] to-[rgba(var(--surface-dark-rgb),0.3)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[rgba(var(--surface-dark-rgb),0.25)] via-transparent to-[rgba(var(--surface-dark-rgb),0.85)]" />
        <NetworkOverlay />
      </div>

      <div className="relative z-10 flex min-h-[calc(80svh-4rem)] items-start md:min-h-0 md:items-center">
        <motion.div
          className="mx-auto max-w-6xl px-6 text-center md:text-left"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <Badge>Enterprise data center, cloud, and managed IT services</Badge>
          </motion.div>

          <motion.h1
            variants={itemVariants}
            style={{
              textShadow:
                "0 0 1px rgba(0,0,0,1), 0 0 3px rgba(0,0,0,1), 0 1px 4px rgba(0,0,0,0.95), 0 2px 10px rgba(0,0,0,0.9), 0 6px 20px rgba(0,0,0,0.7)",
            }}
            className="mx-auto max-w-5xl text-4xl font-bold leading-[1.08] text-white sm:text-5xl md:mx-0 md:text-6xl"
          >
            India’s trusted <RotatingWord /> partner for secure
            infrastructure.
          </motion.h1>

          <motion.p
            variants={itemVariants}
            style={{
              textShadow:
                "0 0 1px rgba(0,0,0,1), 0 0 2px rgba(0,0,0,1), 0 1px 3px rgba(0,0,0,0.9), 0 4px 12px rgba(0,0,0,0.75)",
            }}
            className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-200 sm:text-lg md:mx-0"
          >
            Colocation, dedicated servers, cloud hosting, and managed security — backed by 24/7 support, compliance-ready operations, and enterprise-grade uptime.
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="mt-8 flex flex-col justify-center gap-4 sm:flex-row md:justify-start"
          >
            <button
              onClick={handleContactClick}
              className="inline-flex items-center justify-center rounded-full bg-[var(--website-secondary-color)] px-8 py-4 text-sm font-semibold text-[var(--on-secondary)] shadow-lg shadow-[rgba(var(--website-secondary-rgb),0.2)] transition hover:scale-[1.02] hover:brightness-110"
            >
              Request a quote
            </button>
            <button
              onClick={() => navigate("/data-centers")}
              className="inline-flex items-center justify-center rounded-full border border-white/30 bg-white/10 px-8 py-4 text-sm font-semibold text-white transition hover:scale-[1.02] hover:bg-white/20"
            >
              Explore data centers
            </button>
          </motion.div>

          <motion.div
            variants={cardsContainerVariants}
            className="mt-10 grid gap-4 text-left [perspective:800px] sm:grid-cols-2 lg:grid-cols-3"
          >
            {ALL_SERVICES.slice(0, 3).map((service) => (
              <TiltCard key={service.pageId} service={service} />
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
